import type { LucideIcon } from "lucide-react";
import { Archive, CalendarCheck, Film, Handshake, Images, Music, ShieldCheck } from "lucide-react";
import type { SummarySectionDefinition } from "../../lib/formSubmissionSummary";

export interface ServiceForm {
  title: string;
  description: string;
  href?: string;
  status: "active" | "coming-soon";
  icon: LucideIcon;
}

export const serviceForms: ServiceForm[] = [
  {
    title: "RSVP Hub Form",
    description: "Share your event details, RSVP preferences, design notes, files, and timing.",
    href: "/rsvp-hub",
    status: "active",
    icon: CalendarCheck,
  },
  {
    title: "Memory Hub Form",
    description: "Prepare the story, content, and visual direction for a custom Memory Hub.",
    href: "/memory-hub",
    status: "active",
    icon: Images,
  },
  {
    title: "Archive Hub Form",
    description: "Organize legacy memories, family history, milestones, and long-term collections.",
    href: "/archive-hub",
    status: "active",
    icon: Archive,
  },
  {
    title: "Memory Media Vault Form",
    description: "Collect upload, gallery, review, and storage details for your media vault.",
    href: "/memory-media-vault",
    status: "active",
    icon: ShieldCheck,
  },
  {
    title: "Signature Song Form",
    description: "Provide the story, tone, names, moments, and references for a custom song.",
    href: "/signature-song",
    status: "active",
    icon: Music,
  },
  {
    title: "Signature Highlight Reel Form",
    description: "Gather video direction, footage notes, music style, pacing, and delivery needs.",
    href: "/signature-highlight-reel",
    status: "active",
    icon: Film,
  },
];

export const privateServiceForms: ServiceForm[] = [
  {
    title: "Partner Referral Form",
    description: "Submit referral details for partners, vendors, planners, and collaborators.",
    href: "/partner-referral",
    status: "active",
    icon: Handshake,
  },
];

export const rsvpSections = [
  { id: "contact", title: "Client Contact Information" },
  { id: "additional-contact", title: "Additional Event Contact (Optional)" },
  { id: "event-details", title: "Event Details" },
  { id: "rsvp-page", title: "RSVP Page Details" },
  { id: "guest-preferences", title: "Guest / RSVP Preferences" },
  { id: "design", title: "Design Preferences" },
  { id: "uploads", title: "Uploads / Inspiration Files" },
  { id: "deadline", title: "Deadline & Special Instructions" },
  { id: "creative-ideas", title: "Special Instructions & Additional Information" },
  { id: "future-services", title: "Future Services Interest" },
  { id: "confirmation", title: "Permission & Confirmation" },
];

export const rsvpSummarySections: SummarySectionDefinition[] = [
  {
    title: "Client Contact Information",
    fields: [
      { name: "fullName", label: "Full Name" },
      { name: "email", label: "Email Address" },
      { name: "phone", label: "Phone Number" },
      { name: "preferredContactMethod", label: "Preferred Contact Method" },
    ],
  },
  {
    title: "Additional Event Contact (Optional)",
    fields: [
      { name: "additionalContactName", label: "Name" },
      { name: "additionalContactPhone", label: "Phone Number" },
      { name: "additionalContactEmail", label: "Email Address" },
    ],
  },
  {
    title: "Event Details",
    fields: [
      { name: "eventType", label: "Event Type" },
      { name: "eventName", label: "Event Name or Honoree Name" },
      { name: "eventDate", label: "Event Date", type: "date" },
      { name: "eventStartTime", label: "Event Start Time" },
      { name: "eventEndTime", label: "Event End Time" },
      { name: "eventLocationName", label: "Event Location Name" },
      { name: "eventAddress", label: "Event Address" },
      { name: "eventVisibility", label: "Is this event public or private?" },
      { name: "eventNotes", label: "Any special notes about the event?" },
    ],
  },
  {
    title: "RSVP Page Details",
    fields: [
      { name: "rsvpTitle", label: "What title should appear on the RSVP Hub?" },
      { name: "welcomeMessage", label: "Short welcome message or event description" },
      { name: "dressCode", label: "Dress code" },
      { name: "registryLink", label: "Registry link" },
      { name: "giftLink", label: "Gift/donation link" },
      { name: "menuDetails", label: "Menu or catering details" },
      { name: "parkingInstructions", label: "Parking instructions" },
      { name: "hotelTravelInfo", label: "Hotel/travel information if applicable" },
    ],
  },
  {
    title: "Guest / RSVP Preferences",
    fields: [
      { name: "rsvpYesNo", label: "Do guests need to RSVP yes or no?" },
      { name: "allowAdditionalGuests", label: "Should guests be able to add additional guests?" },
      { name: "mealOptions", label: "Should guests select meal options?" },
      { name: "customQuestions", label: "Should guests answer custom questions?" },
      { name: "rsvpDeadline", label: "RSVP deadline date", type: "date" },
      { name: "eventQuestionContact", label: "Contact person for event questions" },
      { name: "customQuestionDetails", label: "Custom questions or guest notes" },
    ],
  },
  {
    title: "Design Preferences",
    fields: [
      { name: "preferredColors", label: "Preferred colors" },
      { name: "eventTheme", label: "Event theme/style" },
      { name: "logoFile", label: "Uploaded logo", type: "file" },
      { name: "inspirationImages", label: "Uploaded inspiration images", type: "file" },
      { name: "designAvoid", label: "Any design preferences or things to avoid?" },
    ],
  },
  {
    title: "Uploads / Inspiration Files",
    fields: [
      { name: "rsvpHubPhotos", label: "RSVP Hub Photos / Featured Images", type: "file" },
      { name: "flyerFiles", label: "Flyers or event graphics", type: "file" },
      { name: "wordingFiles", label: "Wording files or documents", type: "file" },
    ],
  },
  {
    title: "Deadline & Special Instructions",
    fields: [
      { name: "desiredCompletionDate", label: "Desired completion date", type: "date" },
      { name: "rushRequest", label: "Is this a rush request?" },
      { name: "additionalNotes", label: "Anything else Kreatethedate should know?" },
    ],
  },
  {
    title: "Special Instructions & Additional Information",
    fields: [
      { name: "creativeIdeas", label: "Any Creative Ideas You Would Like Kreatethedate to Consider?" },
    ],
  },
  {
    title: "Future Services Interest",
    fields: [
      {
        name: "futureServicesInterest",
        label: "Would you like information about additional Kreatethedate services?",
        type: "checkboxGroup",
      },
    ],
  },
  {
    title: "Permission & Confirmation",
    fields: [
      { name: "accurateConfirmation", label: "I confirm that the information provided is accurate.", type: "checkbox" },
      {
        name: "contentPermission",
        label: "I confirm that I have permission to submit any photos, videos, logos, wording, or files provided for this project.",
        type: "checkbox",
      },
      {
        name: "serviceUseConfirmation",
        label: "I understand Kreatethedate will use the submitted information to create my requested service.",
        type: "checkbox",
      },
    ],
  },
  {
    title: "Change Request Notice",
    fields: [
      {
        name: "changeRequestAcknowledgement",
        label: "I understand that major changes requested after the RSVP Hub setup has started may require additional time and/or additional fees.",
        type: "checkbox",
      },
    ],
  },
];

export const memoryHubSections = [
  { id: "contact", title: "Client Contact Information" },
  { id: "event-details", title: "Event Details" },
  { id: "memory-hub-details", title: "Memory Hub Details" },
  { id: "photos-videos", title: "Photos & Videos" },
  { id: "guest-memories", title: "Guest Memories & Messages" },
  { id: "gallery-display", title: "Gallery & Display Preferences" },
  { id: "privacy-sharing", title: "Privacy & Sharing" },
  { id: "content-preferences", title: "Memory Hub Content Preferences" },
  { id: "creative-ideas", title: "Creative Ideas" },
  { id: "future-services", title: "Future Services Interest" },
  { id: "confirmation", title: "Permission & Confirmation" },
];

export const memoryHubSummarySections: SummarySectionDefinition[] = [
  {
    title: "Client Contact Information",
    fields: [
      { name: "fullName", label: "Full Name" },
      { name: "email", label: "Email Address" },
      { name: "phone", label: "Phone Number" },
      { name: "preferredContactMethod", label: "Preferred Contact Method" },
      { name: "additionalContactName", label: "Additional Contact Name" },
      { name: "additionalContactPhone", label: "Additional Contact Phone Number" },
      { name: "additionalContactEmail", label: "Additional Contact Email Address" },
    ],
  },
  {
    title: "Event Details",
    fields: [
      { name: "eventType", label: "Event Type" },
      { name: "eventName", label: "Event Name or Honoree Name" },
      { name: "eventDate", label: "Event Date", type: "date" },
      { name: "eventLocation", label: "Event Location" },
      { name: "eventTiming", label: "Is this for a past event or upcoming event?" },
      { name: "eventDescription", label: "Short Event Description" },
      { name: "memoryHubFor", label: "Who is this Memory Hub for?" },
      { name: "memoryHubForOther", label: "Who This Memory Hub Is For Details" },
      { name: "memoryHubType", label: "Memory Hub Type" },
      { name: "memoryHubTypeOther", label: "Memory Hub Type Details" },
    ],
  },
  {
    title: "Memory Hub Details",
    fields: [
      { name: "memoryHubTitle", label: "What title should appear on the Memory Hub?" },
      { name: "memoryHubIntro", label: "What short message or introduction should appear on the Memory Hub?" },
      { name: "memoryHubFeel", label: "How would you like your Memory Hub to feel?", type: "checkboxGroup" },
      { name: "memoryHubFeelOther", label: "Preferred Style Details" },
      { name: "specialSections", label: "Are there any special sections you would like included?" },
    ],
  },
  {
    title: "Photos & Videos",
    fields: [
      { name: "memoryHubPhotos", label: "Upload Photos for the Memory Hub", type: "file" },
      { name: "memoryHubVideos", label: "Upload Videos for the Memory Hub", type: "file" },
      { name: "additionalMediaLater", label: "Will additional photos or videos be added later?" },
      { name: "mediaContributors", label: "Who will be adding media?", type: "checkboxGroup" },
    ],
  },
  {
    title: "Guest Memories & Messages",
    fields: [
      { name: "guestMessagesIncluded", label: "Would you like guest messages included?" },
      { name: "guestMessagesCollected", label: "Do you already have guest messages collected?" },
    ],
  },
  {
    title: "Gallery & Display Preferences",
    fields: [
      { name: "galleryDisplay", label: "How should photos and videos be displayed?" },
      { name: "captionsIncluded", label: "Would you like captions included?" },
    ],
  },
  {
    title: "Privacy & Sharing",
    fields: [
      { name: "memoryHubSharingPreference", label: "How would you like your Memory Hub shared?" },
      { name: "additionalSharingInstructions", label: "Additional Sharing Instructions" },
    ],
  },
  {
    title: "Memory Hub Content Preferences",
    fields: [
      { name: "mediaUsePreference", label: "How would you like Kreatethedate to use your submitted photos and videos?" },
      { name: "additionalMediaInstructions", label: "Additional Media Instructions" },
      {
        name: "memoryMediaVaultInterest",
        label: "Are you interested in allowing family or guests to contribute photos and videos through a Memory Media Vault?",
      },
    ],
  },
  {
    title: "Creative Ideas",
    fields: [
      {
        name: "featuredMemory",
        label: "Is there a special memory, person, milestone, or story you would like highlighted?",
      },
      { name: "creativeIdeas", label: "Any Creative Ideas You Would Like Kreatethedate to Consider?" },
    ],
  },
  {
    title: "Future Services Interest",
    fields: [
      {
        name: "futureServicesInterest",
        label: "Would you like information about additional Kreatethedate services?",
        type: "checkboxGroup",
      },
    ],
  },
  {
    title: "Permission & Confirmation",
    fields: [
      { name: "accurateConfirmation", label: "I confirm that the information provided is accurate to the best of my knowledge.", type: "checkbox" },
      {
        name: "contentPermission",
        label: "I confirm that I have permission to submit and use any photos, videos, logos, graphics, wording, links, or files provided for this project.",
        type: "checkbox",
      },
      {
        name: "serviceUseConfirmation",
        label: "I understand Kreatethedate will build my Memory Hub based on the information submitted, and missing information may delay the project timeline.",
        type: "checkbox",
      },
    ],
  },
  {
    title: "Change Request Notice",
    fields: [
      {
        name: "changeRequestAcknowledgement",
        label: "I understand that major changes requested after the Memory Hub setup has started may require additional time and/or additional fees.",
        type: "checkbox",
      },
    ],
  },
];

export const archiveHubSections = [
  { id: "contact", title: "Client Contact Information" },
  { id: "archive-details", title: "Archive Details" },
  { id: "purpose", title: "Purpose of the Archive" },
  { id: "timeline", title: "Timeline Structure" },
  { id: "existing-memories", title: "Existing Memories & Events" },
  { id: "future-plans", title: "Future Archive Plans" },
  { id: "design-preferences", title: "Design Preferences" },
  { id: "featured-legacy", title: "Featured Memories & Legacy" },
  { id: "media-content", title: "Media & Content" },
  { id: "future-services", title: "Future Services Interest" },
  { id: "confirmation", title: "Permission & Confirmation" },
];

export const archiveHubSummarySections: SummarySectionDefinition[] = [
  {
    title: "Client Contact Information",
    fields: [
      { name: "fullName", label: "Full Name" },
      { name: "email", label: "Email Address" },
      { name: "phone", label: "Phone Number" },
      { name: "preferredContactMethod", label: "Preferred Contact Method" },
      { name: "additionalContactName", label: "Additional Contact Name" },
      { name: "additionalContactPhone", label: "Additional Contact Phone Number" },
      { name: "additionalContactEmail", label: "Additional Contact Email Address" },
    ],
  },
  {
    title: "Archive Details",
    fields: [
      { name: "archiveTitle", label: "Archive Hub Title" },
      { name: "archiveType", label: "Archive Type" },
      { name: "archiveTypeOther", label: "Archive Type Details" },
    ],
  },
  {
    title: "Purpose of the Archive",
    fields: [
      { name: "archivePurpose", label: "What is the primary purpose of this Archive Hub?" },
      { name: "archivePurposeOther", label: "Archive Purpose Details" },
      {
        name: "archiveContentTypes",
        label: "What types of memories would you like included in your Archive?",
        type: "checkboxGroup",
      },
      { name: "archiveContentTypesOther", label: "Archive Content Type Details" },
      { name: "archiveVision", label: "Story, purpose, or vision for the Archive Hub" },
    ],
  },
  {
    title: "Timeline Structure",
    fields: [
      { name: "archiveStructure", label: "How would you like your Archive organized?" },
      { name: "importantMilestones", label: "Important dates, years, or milestones" },
    ],
  },
  {
    title: "Existing Memories & Events",
    fields: [
      { name: "existingHubs", label: "Do you already have RSVP Hubs or Memory Hubs that should be included in this Archive?" },
      {
        name: "includedKreatethedateServices",
        label: "Will this Archive Hub include any Kreatethedate services?",
        type: "checkboxGroup",
      },
      { name: "existingHubList", label: "Events, Memory Hubs, or RSVP Hubs to include" },
    ],
  },
  {
    title: "Future Archive Plans",
    fields: [
      { name: "futureEventsOverTime", label: "Would you like to continue adding future events to this Archive over time?" },
      { name: "futureArchivePlans", label: "Future events, milestones, or memories to add later" },
    ],
  },
  {
    title: "Design Preferences",
    fields: [
      { name: "archiveFeel", label: "How would you like your Archive Hub to feel?", type: "checkboxGroup" },
      { name: "archiveFeelOther", label: "Archive Hub Feel Details" },
      { name: "preferredColors", label: "Preferred Colors" },
      { name: "designAvoid", label: "Anything you would like us to avoid?" },
    ],
  },
  {
    title: "Featured Memories & Legacy",
    fields: [
      {
        name: "featuredLegacyMemory",
        label: "Is there a specific person, memory, milestone, achievement, or story that should be highlighted?",
      },
      {
        name: "futureGenerationsMemory",
        label: "What memories, stories, people, or moments would you most like this Archive to preserve?",
      },
    ],
  },
  {
    title: "Media & Content",
    fields: [
      { name: "providingMediaNow", label: "Will you be providing photos and videos now?" },
      { name: "archivePhotos", label: "Photos", type: "file" },
      { name: "archiveVideos", label: "Videos", type: "file" },
      { name: "archiveDocuments", label: "Documents", type: "file" },
      { name: "archivePrograms", label: "Programs", type: "file" },
      { name: "archiveFlyers", label: "Flyers", type: "file" },
      { name: "archiveHistoricalMaterials", label: "Historical Materials", type: "file" },
      { name: "archiveMediaInstructions", label: "Additional Media Instructions" },
    ],
  },
  {
    title: "Future Services Interest",
    fields: [
      {
        name: "futureServicesInterest",
        label: "Would you like information about additional Kreatethedate services?",
        type: "checkboxGroup",
      },
    ],
  },
  {
    title: "Permission & Confirmation",
    fields: [
      { name: "accurateConfirmation", label: "I confirm that the information provided is accurate to the best of my knowledge.", type: "checkbox" },
      {
        name: "contentPermission",
        label: "I confirm that I have permission to submit and use any photos, videos, logos, graphics, wording, links, or files provided for this project.",
        type: "checkbox",
      },
      {
        name: "serviceUseConfirmation",
        label: "I understand Kreatethedate will build my Archive Hub based on the information submitted.",
        type: "checkbox",
      },
    ],
  },
  {
    title: "Change Request Notice",
    fields: [
      {
        name: "changeRequestAcknowledgement",
        label: "I understand that major changes requested after setup has started may require additional time and/or additional fees.",
        type: "checkbox",
      },
    ],
  },
];

export const memoryMediaVaultSections = [
  { id: "contact", title: "Client Contact Information" },
  { id: "event-project-details", title: "Event / Project Details" },
  { id: "eligibility", title: "Memory Media Vault Eligibility" },
  { id: "vault-purpose", title: "Vault Purpose" },
  { id: "media-contributors", title: "Media Contributors" },
  { id: "organization-preferences", title: "Media Organization Preferences" },
  { id: "review-approval", title: "Review & Approval Preferences" },
  { id: "privacy-sharing", title: "Privacy & Sharing" },
  { id: "media-types", title: "Media Types" },
  { id: "creative-ideas", title: "Creative Ideas" },
  { id: "future-services", title: "Future Services Interest" },
  { id: "confirmation", title: "Permission & Confirmation" },
];

export const memoryMediaVaultSummarySections: SummarySectionDefinition[] = [
  {
    title: "Client Contact Information",
    fields: [
      { name: "fullName", label: "Full Name" },
      { name: "email", label: "Email Address" },
      { name: "phone", label: "Phone Number" },
      { name: "preferredContactMethod", label: "Preferred Contact Method" },
      { name: "additionalContactName", label: "Additional Contact Name" },
      { name: "additionalContactPhone", label: "Additional Contact Phone Number" },
      { name: "additionalContactEmail", label: "Additional Contact Email Address" },
    ],
  },
  {
    title: "Event / Project Details",
    fields: [
      { name: "eventType", label: "Event Type" },
      { name: "eventName", label: "Event Name or Honoree Name" },
      { name: "eventDate", label: "Event Date", type: "date" },
      { name: "eventTiming", label: "Is this for an upcoming event, past event, or ongoing collection?" },
      { name: "eventLocation", label: "Event Location" },
      { name: "projectDescription", label: "Short project description" },
    ],
  },
  {
    title: "Memory Media Vault Eligibility",
    fields: [
      { name: "connectedService", label: "Which Kreatethedate service is this Memory Media Vault connected to?" },
    ],
  },
  {
    title: "Vault Purpose",
    fields: [
      { name: "vaultPurpose", label: "What is the main purpose of your Memory Media Vault?" },
      { name: "vaultPurposeOther", label: "Vault Purpose Details" },
    ],
  },
  {
    title: "Media Contributors",
    fields: [
      { name: "mediaContributors", label: "Who will be adding photos or videos?" },
      { name: "simpleUploadLink", label: "Do contributors need a simple upload link?" },
    ],
  },
  {
    title: "Media Organization Preferences",
    fields: [
      { name: "organizationPreference", label: "How would you like uploaded media organized?" },
      { name: "organizationInstructions", label: "Additional organization instructions" },
    ],
  },
  {
    title: "Review & Approval Preferences",
    fields: [
      { name: "reviewPreference", label: "Would you like to review uploaded media before anything is used publicly?" },
    ],
  },
  {
    title: "Privacy & Sharing",
    fields: [
      { name: "vaultAccess", label: "How should the Memory Media Vault be accessed?" },
      { name: "privacyInstructions", label: "Additional privacy or sharing instructions" },
    ],
  },
  {
    title: "Media Types",
    fields: [
      { name: "mediaTypes", label: "What types of media do you expect to collect?", type: "checkboxGroup" },
      { name: "mediaTypesOther", label: "Media Type Details" },
    ],
  },
  {
    title: "Creative Ideas",
    fields: [
      { name: "creativeIdeas", label: "Any Creative Ideas You Would Like Kreatethedate to Consider?" },
    ],
  },
  {
    title: "Future Services Interest",
    fields: [
      {
        name: "futureServicesInterest",
        label: "Would you like information about additional Kreatethedate services?",
        type: "checkboxGroup",
      },
    ],
  },
  {
    title: "Permission & Confirmation",
    fields: [
      { name: "accurateConfirmation", label: "I confirm that the information provided is accurate to the best of my knowledge.", type: "checkbox" },
      {
        name: "contentPermission",
        label: "I confirm that I have permission to submit, collect, and use any photos, videos, messages, files, or media provided for this project.",
        type: "checkbox",
      },
      {
        name: "premiumEnhancementConfirmation",
        label: "I understand the Memory Media Vault is a premium enhancement and may depend on the service/package selected.",
        type: "checkbox",
      },
      {
        name: "changeRequestAcknowledgement",
        label: "I understand that major changes requested after setup has started may require additional time and/or additional fees.",
        type: "checkbox",
      },
    ],
  },
];
